/**
 * Departments Hooks
 *
 * TanStack Query hooks for department CRUD operations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../lib/hooks/useApi';
import type {
  DepartmentWithCount,
  DepartmentLookup,
  Department,
  CreateDepartment,
  UpdateDepartment,
} from '@guild/shared';

// Query keys
export const departmentKeys = {
  all: ['departments'] as const,
  lists: () => [...departmentKeys.all, 'list'] as const,
  list: (filters: { search?: string; includeInactive?: boolean }) =>
    [...departmentKeys.lists(), filters] as const,
  lookup: () => [...departmentKeys.all, 'lookup'] as const,
  details: () => [...departmentKeys.all, 'detail'] as const,
  detail: (id: string) => [...departmentKeys.details(), id] as const,
};

/**
 * Fetch departments list with operator counts
 */
export function useDepartments(options?: { search?: string; includeInactive?: boolean }) {
  const api = useApi();

  return useQuery({
    queryKey: departmentKeys.list(options ?? {}),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (options?.search) params.set('search', options.search);
      if (options?.includeInactive) params.set('includeInactive', 'true');

      const queryString = params.toString();
      const endpoint = queryString ? `/api/departments?${queryString}` : '/api/departments';

      return api.get<DepartmentWithCount[]>(endpoint);
    },
  });
}

/**
 * Fetch lightweight departments list for dropdowns
 */
export function useDepartmentLookup() {
  const api = useApi();

  return useQuery({
    queryKey: departmentKeys.lookup(),
    queryFn: () => api.get<DepartmentLookup[]>('/api/departments/lookup'),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes (rarely changes)
  });
}

/**
 * Fetch single department
 */
export function useDepartment(id: string) {
  const api = useApi();

  return useQuery({
    queryKey: departmentKeys.detail(id),
    queryFn: () => api.get<Department>(`/api/departments/${id}`),
    enabled: !!id,
  });
}

/**
 * Create department mutation
 */
export function useCreateDepartment() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateDepartment) =>
      api.post<Department>('/api/departments', data),
    onSuccess: () => {
      // Invalidate all department queries
      queryClient.invalidateQueries({ queryKey: departmentKeys.all });
    },
  });
}

/**
 * Update department mutation
 */
export function useUpdateDepartment() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateDepartment }) =>
      api.put<Department>(`/api/departments/${id}`, data),
    onSuccess: (_, variables) => {
      // Invalidate list and specific detail
      queryClient.invalidateQueries({ queryKey: departmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: departmentKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: departmentKeys.lookup() });
    },
  });
}

/**
 * Delete department mutation (soft delete)
 */
export function useDeleteDepartment() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete<{ id: string }>(`/api/departments/${id}`),
    onSuccess: () => {
      // Invalidate all department queries
      queryClient.invalidateQueries({ queryKey: departmentKeys.all });
    },
  });
}
